import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  MAPLE_OS_EDITION_OPTIONS,
  type MapleMarketApp,
  type MapleOSEditionSku,
} from '@/data/mapleMarketApps';

interface SettingsTabProps {
  product: MapleMarketApp;
  disabled: boolean;
  onSaved: () => Promise<void> | void;
}

export default function SettingsTab({ product, disabled, onSaved }: SettingsTabProps) {
  const { toast } = useToast();
  const [featured, setFeatured] = useState(!!product.featured);
  const [version, setVersion] = useState(product.version);
  const [sizeText, setSizeText] = useState(product.size);
  const [includesWebInstaller, setIncludesWebInstaller] = useState(product.includesWebInstaller);
  const [includesDesktopInstaller, setIncludesDesktopInstaller] = useState(product.includesDesktopInstaller);
  const [includesBareMetalInstaller, setIncludesBareMetalInstaller] = useState(product.includesBareMetalInstaller);
  const [supportedMapleOSEditions, setSupportedMapleOSEditions] = useState<MapleOSEditionSku[]>(product.supportedMapleOSEditions);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFeatured(!!product.featured);
    setVersion(product.version);
    setSizeText(product.size);
    setIncludesWebInstaller(product.includesWebInstaller);
    setIncludesDesktopInstaller(product.includesDesktopInstaller);
    setIncludesBareMetalInstaller(product.includesBareMetalInstaller);
    setSupportedMapleOSEditions(product.supportedMapleOSEditions);
  }, [product]);

  const handleEditionToggle = (edition: MapleOSEditionSku, checked: boolean) => {
    setSupportedMapleOSEditions((current) => {
      if (checked) {
        return current.includes(edition) ? current : [...current, edition];
      }

      return current.filter((value) => value !== edition);
    });
  };

  const handleSave = async () => {
    if (supportedMapleOSEditions.length === 0) {
      toast({
        title: 'Choose at least one MapleOS edition',
        description: 'Developers need to allow at least one MapleOS edition for installation.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    const { error } = await (supabase.from('marketplace_products') as any)
      .update({
        featured,
        version: version.trim() || product.version,
        size_text: sizeText.trim() || null,
        includes_web_installer: includesWebInstaller,
        includes_desktop_installer: includesDesktopInstaller,
        includes_bare_metal_installer: includesBareMetalInstaller,
        supported_mapleos_editions: supportedMapleOSEditions,
      })
      .eq('id', product.id);

    setSaving(false);

    if (error) {
      toast({
        title: 'Save failed',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    await onSaved();
    toast({
      title: 'Settings updated',
      description: 'Marketplace flags, installer availability, and MapleOS edition restrictions were saved.',
    });
  };

  const toggles = [
    {
      title: 'Web install flow available',
      description: 'Allows MapleOS to offer in-OS installs for either a single HTML bundle or an uploaded .mpak package.',
      checked: includesWebInstaller,
      onChange: setIncludesWebInstaller,
    },
    {
      title: 'Desktop installer available',
      description: 'Marks the product as having a desktop installer option.',
      checked: includesDesktopInstaller,
      onChange: setIncludesDesktopInstaller,
    },
    {
      title: 'Bare metal installer available',
      description: 'Expose bare metal / recovery installation availability.',
      checked: includesBareMetalInstaller,
      onChange: setIncludesBareMetalInstaller,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Settings</h3>
          <p className="text-sm text-muted-foreground">Control how this product appears and which install options are surfaced.</p>
        </div>
        <Button type="button" onClick={() => void handleSave()} disabled={disabled || saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save changes
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="market-version">Current version</Label>
          <Input id="market-version" value={version} onChange={(event) => setVersion(event.target.value)} disabled={disabled} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-size">Size label</Label>
          <Input id="market-size" value={sizeText} onChange={(event) => setSizeText(event.target.value)} disabled={disabled} />
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-border p-4">
        {toggles.map((toggle) => (
          <div key={toggle.title} className="flex items-center justify-between gap-4 rounded-lg bg-muted/40 px-4 py-3">
            <div>
              <div className="font-medium text-foreground">{toggle.title}</div>
              <div className="text-sm text-muted-foreground">{toggle.description}</div>
            </div>
            <Switch checked={toggle.checked} onCheckedChange={toggle.onChange} disabled={disabled} />
          </div>
        ))}
      </div>

      <div className="space-y-4 rounded-xl border border-border p-4">
        <div>
          <div className="font-medium text-foreground">Supported MapleOS editions</div>
          <div className="text-sm text-muted-foreground">
            Choose which MapleOS editions are allowed to install this app. If you select only Pro or Enterprise, other editions will be blocked from installing it.
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {MAPLE_OS_EDITION_OPTIONS.map((edition) => (
            <label
              key={edition.value}
              className="flex items-start gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm"
            >
              <Checkbox
                checked={supportedMapleOSEditions.includes(edition.value)}
                onCheckedChange={(checked) => handleEditionToggle(edition.value, checked === true)}
                disabled={disabled}
              />
              <span className="font-medium text-foreground">{edition.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}